import { Badge, Box, Flex, HStack, IconButton, Text } from "components/ui";
import { toaster } from "components/ui/toaster";
import {
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTrigger,
} from "components/ui/menu";
import { useEffect, useState } from "react";
import { MdAdd, MdContentCopy, MdDelete, MdMoreVert } from "react-icons/md";
import serverGenerationHubService, {
  ServerProgressUpdate,
} from "services/serverGenerationHubService";
import { ServerConfig, serverService } from "services/serverService";
import CreateServerModal from "./CreateServerModal";

interface ServerSelectorProps {
  selectedServerId: string | undefined;
  onSelect: (id: string) => void;
  onGenerationComplete?: (serverId: string) => void;
}

const ServerSelector = ({
  selectedServerId,
  onSelect,
  onGenerationComplete,
}: ServerSelectorProps) => {
  const [servers, setServers] = useState<ServerConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadServers();

    const initHub = async () => {
      try {
        await serverGenerationHubService.start();
      } catch {
        // ignore
      }
    };
    initHub();

    return () => {
      serverGenerationHubService.stop();
    };
  }, []);

  // Subscribe to progress updates for in-progress servers
  useEffect(() => {
    const handleProgress = (update: ServerProgressUpdate) => {
      setServers((prev) =>
        prev.map((s) =>
          s.id === update.serverId
            ? {
                ...s,
                processingProgress: update.progress,
                isActive: update.progress === 100 ? true : s.isActive,
                processedModels: update.processedModels,
                totalModels: update.totalModels,
                processedTools: update.processedTools,
                totalTools: update.totalTools,
              }
            : s,
        ),
      );
      if (update.progress === 100) {
        onGenerationComplete?.(update.serverId);
        if ((update.suggestedMaskingRulesCount ?? 0) > 0) {
          toaster.create({
            type: "info",
            title: "New masking rules suggested",
            description: `Hey! We analyzed your toolkit's fields and spotted some potentially sensitive data. We've prepared ${update.suggestedMaskingRulesCount} masking rule(s) to help keep it safe — head to Security › Data Masking to activate the ones you need.`,
            duration: 10000,
          });
        }
      }
    };

    const inProgress = servers.filter(
      (s) => s.processingProgress !== undefined && s.processingProgress < 100,
    );
    inProgress.forEach((s) => {
      if (s.id)
        serverGenerationHubService.subscribeToServer(s.id, handleProgress);
    });

    return () => {
      inProgress.forEach((s) => {
        if (s.id)
          serverGenerationHubService.unsubscribeFromServer(
            s.id,
            handleProgress,
          );
      });
    };
  }, [servers]);

  const loadServers = async () => {
    try {
      setLoading(true);
      const data = await serverService.getAllServers();
      setServers((prev) => {
        return data.map((newServer) => {
          const existing = prev.find((s) => s.id === newServer.id);
          if (
            existing &&
            existing.processingProgress !== undefined &&
            existing.processingProgress < 100
          ) {
            return {
              ...newServer,
              processingProgress: existing.processingProgress,
              processedModels: existing.processedModels,
              totalModels: existing.totalModels,
              processedTools: existing.processedTools,
              totalTools: existing.totalTools,
            };
          }
          return newServer;
        });
      });
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const copy = await serverService.copyServer(id);
      await loadServers();
      if (copy.id) onSelect(copy.id);
    } catch {
      toaster.create({ type: "error", title: "Failed to copy toolkit" });
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm("Delete this toolkit?")) return;
    try {
      await serverService.deleteServer(id);
      if (selectedServerId === id) onSelect("");
      await loadServers();
    } catch {
      // ignore
    }
  };

  const handleServerCreated = async (serverId?: string) => {
    setIsModalOpen(false);
    await loadServers();
    if (serverId) {
      onSelect(serverId);
      // Subscribe to progress for the newly created server
      const handleProgress = (update: ServerProgressUpdate) => {
        setServers((prev) =>
          prev.map((s) =>
            s.id === update.serverId
              ? {
                  ...s,
                  processingProgress: update.progress,
                  isActive: update.progress === 100 ? true : s.isActive,
                  processedModels: update.processedModels,
                  totalModels: update.totalModels,
                  processedTools: update.processedTools,
                  totalTools: update.totalTools,
                }
              : s,
          ),
        );
        if (update.progress === 100) {
          onGenerationComplete?.(update.serverId);
          if ((update.suggestedMaskingRulesCount ?? 0) > 0) {
            toaster.create({
              type: "info",
              title: "New masking rules suggested",
              description: `Hey! We analyzed your toolkit's fields and spotted some potentially sensitive data. We've prepared ${update.suggestedMaskingRulesCount} masking rule(s) to help keep it safe — head to Security › Data Masking to activate the ones you need.`,
              duration: 10000,
            });
          }
        }
      };
      await serverGenerationHubService.subscribeToServer(
        serverId,
        handleProgress,
      );
    }
  };

  if (loading) {
    return (
      <Box py={3}>
        <Text fontSize="sm" color="var(--text-secondary-color)">
          Loading toolkits…
        </Text>
      </Box>
    );
  }

  return (
    <>
      <Box
        w="full"
        overflowX="auto"
        pb={2}
        css={{
          "&::-webkit-scrollbar": { height: "4px" },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: "2px",
            background: "var(--border-default)",
          },
        }}
      >
        <HStack gap={2.5} align="stretch" w="max-content" minW="full">
          {servers.map((server) => {
            const isSelected = server.id === selectedServerId;
            const inProgress =
              server.processingProgress !== undefined &&
              server.processingProgress < 100;
            const statusDotColor = inProgress
              ? "var(--accent-warning, #F59E0B)"
              : server.isActive
                ? "var(--accent-success, #22C55E)"
                : "var(--text-tertiary-color, #88898E)";
            const statusLabel = inProgress
              ? `${server.processingProgress}%`
              : server.isActive
                ? "Active"
                : "Inactive";
            return (
              <Box
                key={server.id}
                minW="200px"
                maxW="260px"
                p={3.5}
                borderRadius="10px"
                borderWidth="1px"
                borderColor={
                  isSelected
                    ? "var(--brand-primary, #3B82F6)"
                    : "var(--border-default)"
                }
                bg={
                  isSelected ? "rgba(59, 130, 246, 0.06)" : "var(--bg-primary)"
                }
                cursor="pointer"
                onClick={() => onSelect(server.id!)}
                transition="background-color 0.15s, border-color 0.15s"
                _hover={isSelected ? undefined : { bg: "var(--bg-secondary)" }}
              >
                <Flex direction="column" gap={2.5}>
                  {/* Header row: name + actions */}
                  <Flex justify="space-between" align="start" gap={1}>
                    <Text
                      fontSize="sm"
                      fontWeight={500}
                      color="var(--text-primary)"
                      lineClamp={2}
                      flex={1}
                      minW={0}
                      m={0}
                    >
                      {server.name}
                    </Text>
                    <HStack gap={0} flexShrink={0}>
                      <MenuRoot>
                        <MenuTrigger asChild>
                          <IconButton
                            variant="ghost"
                            size="2xs"
                            aria-label="Toolkit actions"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MdMoreVert />
                          </IconButton>
                        </MenuTrigger>
                        <MenuContent>
                          <MenuItem
                            value="copy"
                            onClick={(e: React.MouseEvent) =>
                              handleCopy(server.id!, e)
                            }
                          >
                            <MdContentCopy />
                            Copy
                          </MenuItem>
                          <MenuItem
                            value="delete"
                            onClick={(e: React.MouseEvent) =>
                              handleDelete(server.id!, e)
                            }
                            color="red.500"
                          >
                            <MdDelete />
                            Delete
                          </MenuItem>
                        </MenuContent>
                      </MenuRoot>
                    </HStack>
                  </Flex>

                  {/* Status (dot + mono label) + tools pill */}
                  <HStack gap={2} flexWrap="wrap" align="center">
                    <HStack gap={1.5} align="center">
                      <Box
                        w="6px"
                        h="6px"
                        borderRadius="full"
                        bg={statusDotColor}
                        flexShrink={0}
                      />
                      <Text
                        fontFamily="var(--font-mono)"
                        fontSize="10px"
                        letterSpacing="0.10em"
                        textTransform="uppercase"
                        color="var(--text-secondary-color)"
                        m={0}
                      >
                        {statusLabel}
                      </Text>
                    </HStack>
                    {!inProgress && (
                      <Badge colorPalette="blue" size="sm" variant="subtle">
                        {server.enabledTools ?? 0}/{server.totalTools ?? 0}{" "}
                        tools
                      </Badge>
                    )}
                  </HStack>

                  {/* Progress bar for in-progress servers */}
                  {inProgress && (
                    <Box
                      w="full"
                      h="3px"
                      bg="var(--bg-secondary)"
                      borderRadius="full"
                      overflow="hidden"
                    >
                      <Box
                        h="full"
                        bg="var(--accent-warning, #F59E0B)"
                        w={`${server.processingProgress}%`}
                        transition="width 0.3s ease"
                      />
                    </Box>
                  )}
                </Flex>
              </Box>
            );
          })}

          {/* Add toolkit card */}
          <Box
            minW="120px"
            p={3.5}
            borderRadius="10px"
            borderWidth="1px"
            borderColor="var(--border-default)"
            borderStyle="dashed"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            color="var(--text-secondary-color)"
            _hover={{
              borderColor: "var(--brand-primary, #3B82F6)",
              color: "var(--brand-primary, #3B82F6)",
            }}
            transition="border-color 0.15s, color 0.15s"
            onClick={() => setIsModalOpen(true)}
          >
            <Flex direction="column" align="center" gap={1}>
              <MdAdd size={20} />
              <Text
                fontFamily="var(--font-mono)"
                fontSize="10px"
                letterSpacing="0.10em"
                textTransform="uppercase"
                m={0}
              >
                Add toolkit
              </Text>
            </Flex>
          </Box>
        </HStack>
      </Box>

      <CreateServerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onServerCreated={handleServerCreated}
      />
    </>
  );
};

export default ServerSelector;
